import { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { toUiTask, type UiTask } from "@/components/tasks/types";
import type { Sprint, EndSprintResponse } from "@/services/sprint.service";
import { listAllTasks } from "@/services/task.service";
import { usePermission } from "@/hooks/usePermission";
import { EndSprintModal } from "./EndSprintModal";

const ALLOWED_ROLES = new Set(["admin", "manager"]);

interface Props {
	activeSprint: Sprint | null;
	onEnded: (result: EndSprintResponse) => void;
}

export function EndSprintButton({ activeSprint, onEnded }: Props) {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [sprintTasks, setSprintTasks] = useState<UiTask[]>([]);
	const { roleKey } = usePermission();
	if (!activeSprint) return null;
	if (!roleKey || !ALLOWED_ROLES.has(roleKey)) return null;

	// Fetched fresh on open: the board may be filtered to another sprint.
	async function handleOpen() {
		setLoading(true);
		try {
			const tasks = await listAllTasks({ sprint_id: activeSprint!.id });
			setSprintTasks(tasks.map(toUiTask));
			setOpen(true);
		} catch {
			toast.error("Failed to load sprint tasks", {
				description: "Please try again.",
			});
		} finally {
			setLoading(false);
		}
	}

	return (
		<>
			<Button
				variant="outline"
				className="flex items-center gap-2"
				onClick={handleOpen}
				disabled={loading}
			>
				{loading ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Flag className="h-4 w-4" />
				)}
				End Sprint
			</Button>
			<EndSprintModal
				open={open}
				onClose={() => setOpen(false)}
				sprint={activeSprint}
				tasks={sprintTasks}
				onEnded={onEnded}
			/>
		</>
	);
}
